import { NgClass, NgComponentOutlet, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, Type } from '@angular/core';
import { TableBlock, TableCell } from '../../../model';

@Component({
  selector: 'dojo-table-block-renderer',
  templateUrl: './table-block-renderer.component.html',
  styleUrls: ['./table-block-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, NgClass, NgComponentOutlet],
})
export class TableBlockRendererComponent {
  readonly block = input.required<TableBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();

  readonly processedRows = computed(() =>
    this.block().rows.map((row) => ({
      cells: row.cells.map((cell) => ({
        ...cell,
        styles: this.computeCellStyles(cell),
        classes: this.computeCellClasses(cell),
      })),
    })),
  );

  readonly tableMinWidth = computed(() => {
    const rows = this.block().rows;
    if (!rows.length) {
      return 0;
    }

    const firstRow = rows[0].cells;
    const numColumns = firstRow.reduce((sum, cell) => sum + (cell.colspan || 1), 0);
    const CELL_PADDING = 16;
    const BORDER_ALLOWANCE = 2;

    const colwidthSum = firstRow.reduce((sum, cell) => {
      const width = cell.colwidth?.[0];
      return width ? sum + width : sum;
    }, 0);

    const hasColwidths = firstRow.some((cell) => cell.colwidth?.length);
    const baseWidth = hasColwidths ? colwidthSum : numColumns * 120;
    const fullWidth = baseWidth + numColumns * CELL_PADDING + BORDER_ALLOWANCE;

    return Math.round(fullWidth * 0.5);
  });

  private computeCellStyles(cell: TableCell): NgStyle['ngStyle'] {
    const styles: Record<string, string> = {};

    if (cell.background_color) {
      styles['backgroundColor'] = cell.background_color;
    }

    if (cell.colwidth?.length) {
      styles['width'] = `${cell.colwidth[0]}px`;
    }

    return styles;
  }

  private computeCellClasses(cell: TableCell): NgClass['ngClass'] {
    const classes: string[] = [];

    if (cell.text_align) {
      classes.push(`align-${cell.text_align}`);
    }

    if (cell.vertical_align) {
      classes.push(`valign-${cell.vertical_align}`);
    }

    return classes;
  }
}
